/**
 * Document Filing-field Extractor
 * ---------------------------------
 * One entry point used by the upload flow to auto-fill the Filing tab fields for a
 * freshly-uploaded document. Four engines, all returning the same ParsedReceiptData:
 *
 *  - 'local'       FREE, fully client-side: pdfjs text layer + Tesseract OCR fallback
 *                  + the heuristic parser. Zero cost / offline, but fuzzy on logos.
 *  - 'claude'      PAID, server-side Claude vision (POST /api/extract/document).
 *                  Accurate, reads scans/photos/logos.
 *  - 'self_hosted' Your own vision model (POST /api/extract/document-local). Inert
 *                  until VLM_EXTRACT_URL is set on the server — free once the GPU is up.
 *  - 'auto'        FREE first; escalate only weak results to the configured AI engine
 *                  and merge. Cost-optimal default — most docs never touch the AI.
 *
 * Owner is NEVER extracted here — the document owner is the uploader's internal
 * entity choice (and the uploader may not be the owner, e.g. filing for a spouse).
 */
import apiClient from '../../../lib/api/apiClient';
import { extractTextFromPdf, ocrPdfPages } from './pdfTextExtractor';
import { parseReceiptText, ParsedReceiptData } from './receiptParser';

export type ExtractEngine = 'local' | 'claude' | 'self_hosted' | 'auto';

// Which AI engine the 'auto' route escalates to when the free pass is weak.
// Default = Claude (the server has the key today). Set REACT_APP_EXTRACT_AI_ENGINE
// = 'self_hosted' once your GPU + VLM endpoint is live to escalate for free instead.
const AI_ENGINE: 'claude' | 'self_hosted' =
  process.env.REACT_APP_EXTRACT_AI_ENGINE === 'self_hosted' ? 'self_hosted' : 'claude';

// Server routes for the two AI engines (identical request/response shape).
const AI_ROUTE: Record<'claude' | 'self_hosted', string> = {
  claude: '/extract/document',
  self_hosted: '/extract/document-local',
};

export interface FilingExtractInput {
  engine: ExtractEngine;
  /** gzip-compressed base64 (the stored dms_documents.file_base64) — used by the local pdfjs path. */
  gzipBase64: string;
  /** raw (uncompressed) PDF/image bytes — used by the AI engines. */
  rawBytes?: Uint8Array;
  /** media type for the AI engines (defaults to application/pdf). */
  mediaType?: string;
  /** original filename — helps the local parser (vendor/date in the name). */
  filename?: string;
}

// Raw bytes → base64 (NOT gzip — the AI engines need the real file bytes). Chunked
// to avoid blowing the call-stack on String.fromCharCode for large files.
function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunk)) as any);
  }
  return btoa(binary);
}

interface DocExtractApiResult {
  party_name?: string;
  doc_type?: string;
  doc_date?: string;
  invoice_ref?: string;
  total_amount?: number | null;
  currency?: string;
  payment_method?: string;
  purpose?: string;
  raw_date_string?: string;
  tax_type?: string;
  tax_rate_percent?: number | null;
  tax_amount?: number | null;
  business_address?: string;
  business_phone?: string;
  business_email?: string;
  business_website?: string;
  business_category?: string;
  devMock?: boolean;
  notConfigured?: boolean;
}

function apiResultToParsed(d: DocExtractApiResult): ParsedReceiptData {
  return {
    partyName: d.party_name || '',
    invoiceRef: d.invoice_ref || '',
    dateOfDocument: d.doc_date || '',
    totalAmount: typeof d.total_amount === 'number' ? String(d.total_amount) : '',
    currency: d.currency || '',
    paymentMethod: d.payment_method || '',
    purpose: d.purpose || '',
    docTypeHint: d.doc_type || 'Other Document',
    rawText: '',
    rawDateString: d.raw_date_string || '',
    taxType: d.tax_type || '',
    taxRatePercent: typeof d.tax_rate_percent === 'number' ? String(d.tax_rate_percent) : '',
    taxAmount: typeof d.tax_amount === 'number' ? String(d.tax_amount) : '',
    businessAddress: d.business_address || '',
    businessPhone: d.business_phone || '',
    businessEmail: d.business_email || '',
    businessWebsite: d.business_website || '',
    businessCategory: d.business_category || '',
  };
}

// POST the raw file to a server-side AI extraction route (Claude or self-hosted).
async function callAiExtract(route: string, rawBytes: Uint8Array | undefined, mediaType: string): Promise<ParsedReceiptData> {
  if (!rawBytes) throw new Error('AI extraction requires raw file bytes');
  const fileBase64 = bytesToBase64(rawBytes);
  const resp = await apiClient.post<DocExtractApiResult>(route, { fileBase64, mediaType });
  return apiResultToParsed(resp.data || {});
}

// "Weak" free result → worth escalating to AI. The free engine's documented blind
// spot is the vendor name in a logo (it can't OCR a stylized wordmark), so a missing
// party is the primary trigger; a doc with neither amount nor date is a near-total
// miss (e.g. a scan whose OCR produced garbage). Tune here to trade cost vs accuracy.
function isLowConfidence(p: ParsedReceiptData): boolean {
  if (!p.partyName) return true;
  if (!p.totalAmount && !p.dateOfDocument) return true;
  return false;
}

// AI result wins field-by-field, but keep any free-pass value the AI left blank.
function mergeParsed(local: ParsedReceiptData, ai: ParsedReceiptData): ParsedReceiptData {
  return {
    partyName: ai.partyName || local.partyName,
    invoiceRef: ai.invoiceRef || local.invoiceRef,
    dateOfDocument: ai.dateOfDocument || local.dateOfDocument,
    totalAmount: ai.totalAmount || local.totalAmount,
    currency: ai.currency || local.currency,
    paymentMethod: ai.paymentMethod || local.paymentMethod,
    purpose: ai.purpose || local.purpose,
    docTypeHint: ai.docTypeHint || local.docTypeHint,
    rawText: local.rawText || ai.rawText,
    rawDateString: ai.rawDateString || local.rawDateString,
    taxType: ai.taxType || local.taxType,
    taxRatePercent: ai.taxRatePercent || local.taxRatePercent,
    taxAmount: ai.taxAmount || local.taxAmount,
    businessAddress: ai.businessAddress || local.businessAddress,
    businessPhone: ai.businessPhone || local.businessPhone,
    businessEmail: ai.businessEmail || local.businessEmail,
    businessWebsite: ai.businessWebsite || local.businessWebsite,
    businessCategory: ai.businessCategory || local.businessCategory,
    // The AI schema returns one address string; the structured letterhead fields
    // (city / state / postal code / fax / registration numbers) come from the local read.
    businessCity: ai.businessCity || local.businessCity,
    businessState: ai.businessState || local.businessState,
    businessPostalCode: ai.businessPostalCode || local.businessPostalCode,
    businessFax: ai.businessFax || local.businessFax,
    businessMobile: ai.businessMobile || local.businessMobile,
    businessTaxId: ai.businessTaxId || local.businessTaxId,
    businessTaxIdLabel: ai.businessTaxIdLabel || local.businessTaxIdLabel,
    businessTaxId2: ai.businessTaxId2 || local.businessTaxId2,
    businessTaxId2Label: ai.businessTaxId2Label || local.businessTaxId2Label,
  };
}

/**
 * Extract Filing-tab fields for one document. Returns ParsedReceiptData regardless
 * of engine. Best-effort: throws are the caller's to catch (upload should never fail
 * because extraction failed).
 */
export async function extractFilingFields(input: FilingExtractInput): Promise<ParsedReceiptData> {
  const { engine, gzipBase64, rawBytes, mediaType = 'application/pdf', filename = '' } = input;

  if (engine === 'claude') return callAiExtract(AI_ROUTE.claude, rawBytes, mediaType);
  if (engine === 'self_hosted') return callAiExtract(AI_ROUTE.self_hosted, rawBytes, mediaType);

  if (engine === 'auto') {
    const local = await localExtractFilingFields(gzipBase64, filename);
    if (!isLowConfidence(local)) return local;   // free pass was good enough — no AI spend
    try {
      const ai = await callAiExtract(AI_ROUTE[AI_ENGINE], rawBytes, mediaType);
      return mergeParsed(local, ai);
    } catch {
      return local;   // AI unavailable/failed → keep the free result
    }
  }

  // 'local' (default) — text layer + Tesseract OCR fallback (see localExtractFilingFields).
  return localExtractFilingFields(gzipBase64, filename);
}

// Below this many non-space chars, treat the PDF as a scan/photo with no usable
// text layer → OCR the whole page(s) rather than the (near-empty) text layer.
const TEXT_LAYER_MIN_CHARS = 40;

/**
 * FREE local field extraction with a smart OCR fallback (Tesseract.js):
 *   1. Read the PDF text layer (instant, accurate for digital PDFs).
 *   2. No usable text layer (a scan/photo) → OCR the pages, then parse.
 *   3. Text layer present but the issuer wasn't found (often a logo image the
 *      text layer can't see, e.g. "SaskEnergy") → OCR just the page-1 header and
 *      re-parse the merged text.
 * OCR therefore only fires when the cheap text pass falls short, so digital PDFs
 * stay fast. Every OCR call is best-effort and never throws to the caller.
 */
export async function localExtractFilingFields(gzipBase64: string, filename = ''): Promise<ParsedReceiptData> {
  let text = '';
  try { text = await extractTextFromPdf(gzipBase64); } catch { text = ''; }
  const textLen = text.replace(/\s/g, '').length;

  // 2. Scan / image-only PDF → OCR up to a few pages.
  if (textLen < TEXT_LAYER_MIN_CHARS) {
    const ocrText = await ocrPdfPages(gzipBase64, { maxPages: 3 });
    return parseReceiptText(ocrText || text, filename);
  }

  // 3. Has a text layer. Parse it; if the issuer is still unknown, OCR the header.
  let parsed = parseReceiptText(text, filename);
  if (!parsed.partyName) {
    const header = await ocrPdfPages(gzipBase64, { maxPages: 1, topFraction: 0.3 });
    if (header) parsed = parseReceiptText(`${header}\n${text}`, filename);
  }
  return parsed;
}

// ── doc-type hint → real acctg_docu_type id ──────────────────────────────────
// The parser/Claude hint is a human-ish name; map it to a seeded acctg_docu_type.
// Conservative synonyms for hints that aren't literal table names; everything
// unknown falls back to "Other Document" (never leaves the type unguessed).
const DOCTYPE_SYNONYMS: Record<string, string> = {
  'bill': 'Purchase Invoice',
  'invoice': 'Purchase Invoice',
  'credit note': 'Credit Memo',
  'bank statement': 'Statement',
  'quotation': 'Other Document',
  'contract': 'Other Document',
  'agreement': 'Other Document',
  'lease agreement': 'Other Document',
  'mortgage document': 'Other Document',
  'tax document': 'Other Document',
  'pay stub': 'Other Document',
  'insurance document': 'Other Document',
};

/**
 * Resolve an extracted doc-type hint (e.g. "Receipt", "Bank Statement") to a real
 * acctg_docu_type id. Returns null only if even "Other Document" is missing from
 * the table (so a doc never gets a wrong type forced onto it). `docuTypes` are the
 * raw acctg_docu_type rows (loadTable output).
 */
export function resolveDocuTypeId(typeHint: string, docuTypes: Record<string, any>[]): number | null {
  const active = docuTypes.filter(dt => Number(dt.is_deleted) !== 1);
  const byName = (name: string): Record<string, any> | undefined =>
    active.find(dt => String(dt.docu_type_name || '').trim().toLowerCase() === name.trim().toLowerCase());

  const hint = (typeHint || '').trim().toLowerCase();
  if (!hint) return byName('Other Document')?.docu_type_id ?? null;

  // 1. Exact match against a real type name.
  const exact = byName(hint);
  if (exact) return exact.docu_type_id;

  // 2. Known synonym → real type name.
  const mapped = DOCTYPE_SYNONYMS[hint];
  if (mapped) {
    const m = byName(mapped);
    if (m) return m.docu_type_id;
  }

  // 3. Fallback.
  return byName('Other Document')?.docu_type_id ?? null;
}
